import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography,
  Box,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Alert,
  CircularProgress,
  Avatar,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
} from '@mui/material';
import {
  Search as SearchIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  School as SchoolIcon,
  Star as StarIcon,
  TrendingUp as TrendingUpIcon,
  Visibility as ViewIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { collection, getDocs, query, orderBy, doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';

interface User {
  uid: string;
  email: string;
  firstName: string;
  lastName: string;
  displayName: string;
  totalScore: number;
  ranking: number;
  profileImageUrl: string;
  school: string;
  grade: string;
  city: string;
  age: number | null;
  createdAt: any;
  lastLoginAt: any;
  isActive: boolean;
}

const Users: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [gradeFilter, setGradeFilter] = useState<string>('all');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showUserDialog, setShowUserDialog] = useState(false);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError('');

      const q = query(collection(db, 'users'), orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      
      const usersData = querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          uid: doc.id,
          email: data.email || '',
          firstName: data.firstName || '',
          lastName: data.lastName || '',
          displayName: data.displayName || `${data.firstName || ''} ${data.lastName || ''}`.trim() || 'İsimsiz Kullanıcı',
          totalScore: data.totalScore || 0,
          ranking: data.ranking || 0,
          profileImageUrl: data.profileImageUrl || '',
          school: data.school || '',
          grade: data.grade || '',
          city: data.city || '',
          age: data.age || null,
          createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(),
          lastLoginAt: data.lastLoginAt?.toDate ? data.lastLoginAt.toDate() : null,
          isActive: data.isActive !== false, // Default to true if not specified
        };
      }) as User[];

      setUsers(usersData);
      setFilteredUsers(usersData);
    } catch (error) {
      console.error('Error fetching users:', error);
      setError('Kullanıcılar yüklenirken hata oluştu. Lütfen sayfayı yenileyin.');
    } finally {
      setLoading(false);
    }
  };

  const filterUsers = useCallback(() => {
    let filtered = users;

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(user =>
        user.displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.school.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.city.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(user => {
        if (statusFilter === 'active') return user.isActive;
        if (statusFilter === 'inactive') return !user.isActive;
        return true;
      });
    }

    // Grade filter
    if (gradeFilter !== 'all') {
      filtered = filtered.filter(user => user.grade === gradeFilter);
    }

    setFilteredUsers(filtered);
  }, [users, searchTerm, statusFilter, gradeFilter]);

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    filterUsers();
  }, [filterUsers]);

  const handleToggleUserStatus = async (user: User) => {
    try {
      await updateDoc(doc(db, 'users', user.uid), {
        isActive: !user.isActive
      });

      // Update local state
      setUsers(users.map(u => 
        u.uid === user.uid ? { ...u, isActive: !u.isActive } : u
      ));
    } catch (error) {
      console.error('Error updating user status:', error);
      setError('Kullanıcı durumu güncellenirken hata oluştu');
    }
  };

  const handleViewUser = (user: User) => {
    setSelectedUser(user);
    setShowUserDialog(true);
  };

  const formatDate = (date: Date | undefined) => {
    if (!date) return 'Belirtilmemiş';
    return new Date(date).toLocaleString('tr-TR');
  };

  const getScoreColor = (score: number) => {
    if (score >= 1000) return '#10B981';
    if (score >= 500) return '#F59E0B';
    if (score >= 100) return '#3B82F6';
    return '#6B7280';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: '100%', mx: 'auto' }}>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h3" sx={{ 
          fontWeight: 700, 
          mb: 1,
          color: '#1E293B'
        }}>
          Kullanıcı Yönetimi
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Kayıtlı kullanıcıları görüntüleyin, filtreleyin ve yönetin
        </Typography>

        {/* Stats Cards */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 4 }}>
          <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)', md: '1 1 calc(25% - 18px)' } }}>
            <Card sx={{ 
              borderRadius: 3,
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
            }}>
              <CardContent sx={{ color: 'white', textAlign: 'center' }}>
                <PersonIcon sx={{ fontSize: 40, mb: 1 }} />
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                  {users.length}
                </Typography>
                <Typography variant="body2">
                  Toplam Kullanıcı
                </Typography>
              </CardContent>
            </Card>
          </Box>
          
          <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)', md: '1 1 calc(25% - 18px)' } }}>
            <Card sx={{ 
              borderRadius: 3,
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)'
            }}>
              <CardContent sx={{ color: 'white', textAlign: 'center' }}>
                <CheckCircleIcon sx={{ fontSize: 40, mb: 1 }} />
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                  {users.filter(u => u.isActive).length}
                </Typography>
                <Typography variant="body2">
                  Aktif Kullanıcı
                </Typography>
              </CardContent>
            </Card>
          </Box>
          
          <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)', md: '1 1 calc(25% - 18px)' } }}>
            <Card sx={{ 
              borderRadius: 3,
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)'
            }}>
              <CardContent sx={{ color: 'white', textAlign: 'center' }}>
                <StarIcon sx={{ fontSize: 40, mb: 1 }} />
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                  {users.reduce((sum, user) => sum + user.totalScore, 0)}
                </Typography>
                <Typography variant="body2">
                  Toplam Puan
                </Typography>
              </CardContent>
            </Card>
          </Box>
          
          <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)', md: '1 1 calc(25% - 18px)' } }}>
            <Card sx={{ 
              borderRadius: 3,
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)'
            }}>
              <CardContent sx={{ color: 'white', textAlign: 'center' }}>
                <TrendingUpIcon sx={{ fontSize: 40, mb: 1 }} />
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                  {users.filter(u => u.totalScore > 0).length}
                </Typography>
                <Typography variant="body2">
                  Aktif Oyuncu
                </Typography>
              </CardContent>
            </Card>
          </Box>
        </Box>

        {/* Filters */}
        <Box sx={{ 
          display: 'flex', 
          gap: 2, 
          mb: 4,
          flexWrap: 'wrap',
          alignItems: 'center'
        }}>
          <TextField
            placeholder="Kullanıcı ara..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ minWidth: 300 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Durum</InputLabel>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              label="Durum"
            >
              <MenuItem value="all">Tümü</MenuItem>
              <MenuItem value="active">Aktif</MenuItem>
              <MenuItem value="inactive">Pasif</MenuItem>
            </Select>
          </FormControl>
          
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Sınıf</InputLabel>
            <Select
              value={gradeFilter}
              onChange={(e) => setGradeFilter(e.target.value)}
              label="Sınıf"
            >
              <MenuItem value="all">Tümü</MenuItem>
              <MenuItem value="5">5. Sınıf</MenuItem>
              <MenuItem value="6">6. Sınıf</MenuItem>
              <MenuItem value="7">7. Sınıf</MenuItem>
              <MenuItem value="8">8. Sınıf</MenuItem>
            </Select>
          </FormControl>
          
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchUsers}
            disabled={loading}
          >
            Yenile
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {/* Users Table */}
      <Card sx={{ 
        borderRadius: 3,
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        overflow: 'hidden'
      }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                <TableCell sx={{ fontWeight: 600 }}>Kullanıcı</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>İletişim</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Okul Bilgileri</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Puan & Sıralama</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Kayıt Tarihi</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Son Giriş</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Durum</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>İşlemler</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                      {searchTerm || statusFilter !== 'all' || gradeFilter !== 'all' 
                        ? 'Filtrelere uygun kullanıcı bulunamadı' 
                        : 'Henüz kullanıcı bulunmuyor'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredUsers.map((user) => (
                  <TableRow 
                    key={user.uid}
                    sx={{ 
                      '&:hover': { 
                        backgroundColor: '#f8f9fa',
                        transition: 'background-color 0.2s ease'
                      } 
                    }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar 
                          src={user.profileImageUrl} 
                          sx={{ width: 40, height: 40 }}
                        >
                          {user.displayName.charAt(0).toUpperCase()}
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            {user.displayName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            ID: {user.uid.substring(0, 8)}...
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    
                    <TableCell>
                      <Box>
                        <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <EmailIcon sx={{ fontSize: 16 }} />
                          {user.email}
                        </Typography>
                        {user.age && (
                          <Typography variant="caption" color="text.secondary">
                            {user.age} yaşında
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    
                    <TableCell>
                      <Box>
                        <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <SchoolIcon sx={{ fontSize: 16 }} />
                          {user.school || 'Belirtilmemiş'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {user.grade ? `${user.grade}. Sınıf` : 'Sınıf belirtilmemiş'} • {user.city || 'Şehir belirtilmemiş'}
                        </Typography>
                      </Box>
                    </TableCell>
                    
                    <TableCell>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: getScoreColor(user.totalScore) }}>
                          {user.totalScore} puan
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Sıralama: #{user.ranking || 'N/A'}
                        </Typography>
                      </Box>
                    </TableCell>
                    
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(user.createdAt)}
                      </Typography>
                    </TableCell>
                    
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(user.lastLoginAt)}
                      </Typography>
                    </TableCell>
                    
                    <TableCell>
                      <Chip 
                        label={user.isActive ? 'Aktif' : 'Pasif'} 
                        size="small"
                        sx={{
                          backgroundColor: user.isActive ? '#10B981' : '#94A3B8',
                          color: 'white',
                          fontWeight: 600
                        }}
                      />
                    </TableCell>
                    
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="Kullanıcı Detayları">
                          <IconButton
                            size="small"
                            onClick={() => handleViewUser(user)}
                            sx={{ color: '#3B82F6' }}
                          >
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={user.isActive ? 'Kullanıcıyı Pasif Yap' : 'Kullanıcıyı Aktif Yap'}>
                          <IconButton
                            size="small"
                            onClick={() => handleToggleUserStatus(user)}
                            sx={{ color: user.isActive ? '#EF4444' : '#10B981' }}
                          >
                            {user.isActive ? <BlockIcon /> : <CheckCircleIcon />}
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* User Detail Dialog */}
      <Dialog 
        open={showUserDialog} 
        onClose={() => setShowUserDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
          }
        }}
      >
        <DialogTitle sx={{ 
          backgroundColor: '#2563EB',
          color: 'white',
          py: 3,
          px: 4
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <PersonIcon sx={{ fontSize: 24 }} />
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              Kullanıcı Detayları
            </Typography>
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ p: 4 }}>
          {selectedUser && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 calc(33.333% - 16px)' } }}>
                <Box sx={{ textAlign: 'center', mb: 3 }}>
                  <Avatar 
                    src={selectedUser.profileImageUrl} 
                    sx={{ width: 120, height: 120, mx: 'auto', mb: 2 }}
                  >
                    {selectedUser.displayName.charAt(0).toUpperCase()}
                  </Avatar>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {selectedUser.displayName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedUser.email}
                  </Typography>
                </Box>
              </Box>
              
              <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 calc(66.667% - 16px)' } }}>
                <Stack spacing={3}>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                      Temel Bilgiler
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                      <Box sx={{ flex: '1 1 calc(50% - 8px)' }}>
                        <Typography variant="body2" color="text.secondary">
                          Ad Soyad
                        </Typography>
                        <Typography variant="body1">
                          {selectedUser.firstName} {selectedUser.lastName}
                        </Typography>
                      </Box>
                      <Box sx={{ flex: '1 1 calc(50% - 8px)' }}>
                        <Typography variant="body2" color="text.secondary">
                          Yaş
                        </Typography>
                        <Typography variant="body1">
                          {selectedUser.age || 'Belirtilmemiş'}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                  
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                      Okul Bilgileri
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                      <Box sx={{ flex: '1 1 calc(50% - 8px)' }}>
                        <Typography variant="body2" color="text.secondary">
                          Okul
                        </Typography>
                        <Typography variant="body1">
                          {selectedUser.school || 'Belirtilmemiş'}
                        </Typography>
                      </Box>
                      <Box sx={{ flex: '1 1 calc(50% - 8px)' }}>
                        <Typography variant="body2" color="text.secondary">
                          Sınıf
                        </Typography>
                        <Typography variant="body1">
                          {selectedUser.grade ? `${selectedUser.grade}. Sınıf` : 'Belirtilmemiş'}
                        </Typography>
                      </Box>
                      <Box sx={{ flex: '1 1 calc(50% - 8px)' }}>
                        <Typography variant="body2" color="text.secondary">
                          Şehir
                        </Typography>
                        <Typography variant="body1">
                          {selectedUser.city || 'Belirtilmemiş'}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                  
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                      Oyun İstatistikleri
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                      <Box sx={{ flex: '1 1 calc(50% - 8px)' }}>
                        <Typography variant="body2" color="text.secondary">
                          Toplam Puan
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600, color: getScoreColor(selectedUser.totalScore) }}>
                          {selectedUser.totalScore}
                        </Typography>
                      </Box>
                      <Box sx={{ flex: '1 1 calc(50% - 8px)' }}>
                        <Typography variant="body2" color="text.secondary">
                          Sıralama
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          #{selectedUser.ranking || 'N/A'}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                  
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                      Hesap Bilgileri
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                      <Box sx={{ flex: '1 1 calc(50% - 8px)' }}>
                        <Typography variant="body2" color="text.secondary">
                          Kayıt Tarihi
                        </Typography>
                        <Typography variant="body1">
                          {formatDate(selectedUser.createdAt)}
                        </Typography>
                      </Box>
                      <Box sx={{ flex: '1 1 calc(50% - 8px)' }}>
                        <Typography variant="body2" color="text.secondary">
                          Son Giriş
                        </Typography>
                        <Typography variant="body1">
                          {formatDate(selectedUser.lastLoginAt)}
                        </Typography>
                      </Box>
                      <Box sx={{ flex: '1 1 calc(50% - 8px)' }}>
                        <Typography variant="body2" color="text.secondary">
                          Durum
                        </Typography>
                        <Chip 
                          label={selectedUser.isActive ? 'Aktif' : 'Pasif'} 
                          size="small"
                          sx={{
                            backgroundColor: selectedUser.isActive ? '#10B981' : '#94A3B8',
                            color: 'white',
                            fontWeight: 600
                          }}
                        />
                      </Box>
                    </Box>
                  </Box>
                </Stack>
              </Box>
            </Box>
          )}
        </DialogContent>
        
        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button 
            onClick={() => setShowUserDialog(false)}
            variant="outlined"
            sx={{
              borderColor: '#64748B',
              color: '#64748B',
              px: 4,
              py: 1.5,
              borderRadius: 2,
              '&:hover': {
                borderColor: '#475569',
                backgroundColor: '#F1F5F9',
              }
            }}
          >
            Kapat
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Users; 